import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BaseServices } from 'src/app/kuba/kuba.services';
import { FlexiComplianceService } from 'src/app/kuba/flexi/compliance/flexi-compliance.service';
import {
  FlexiCompliance,
  smileyImage,
  smileyLabelKey
} from 'src/app/kuba/flexi/compliance/flexi-compliance.models';

/**
 * Business-dashboard widget — the business's OWN current OHS Compliance
 * Review (BHT-samsvar), as a numeric score plus a segmented ring.
 *
 * The ring is filled out of 100 %, not out of "however many checkpoints were
 * rated": each segment is the weight share of one rating, and gray closes the
 * gap to 100 %.
 *   green  — compliant / approved
 *   yellow — needs improvement (warning)
 *   red    — missing (critical)
 *   gray   — not yet assessed, plus any rounding remainder
 * The number in the hole stays the weighted score, which is a different
 * quantity: it gives yellow half credit, so it never simply equals the green
 * segment.
 *
 * Data source: the customer-facing <c>GET /flexi/compliance/{businessId}/final</c>
 * (the same endpoint the read-only customer view uses), which already carries
 * the server-computed <c>score</c> — counts, shares, and percentage. No CSM
 * rights needed — every business user (Editor / User) can see their own score.
 *
 * Visibility: the dashboard only renders this widget for a business that has
 * Flexi enabled; the widget itself shows a neutral empty state until the HSE
 * advisor has completed a review (nothing to score before then).
 */
@Component({
  selector: 'app-flexi-compliance-business-widget',
  templateUrl: './flexi-compliance-business-widget.component.html',
  styleUrls: ['./flexi-compliance-business-widget.component.scss'],
  standalone: false
})
export class FlexiComplianceBusinessWidgetComponent implements OnInit {
  loading = true;

  green = 0;
  yellow = 0;
  red = 0;
  unrated = 0;
  total = 0;
  percentage = 0;
  smiley: 'red' | 'yellow' | 'green' | null = null;
  completedOn: string | null = null;

  /** ngx-charts inputs. customColors pins traffic-light hues by slice name. */
  chartData: { name: string; value: number }[] = [];
  customColors: { name: string; value: string }[] = [];

  // Larger, easier-to-read donut. The host cell wraps the chips below it on
  // narrow screens (see SCSS), so a fixed 220px square reads well from desktop
  // down to mobile without overlapping the legend/chips.
  view: [number, number] = [220, 220];
  gradient = false;
  showLegend = false;
  showLabels = false;
  isDoughnut = true;

  // Green, Yellow, Red, Gray — matches the smiley pack, the chips below the
  // ring, and the portal widget.
  static readonly GREEN = '#15803d';
  static readonly YELLOW = '#ca8a04';
  static readonly RED = '#dc2626';
  static readonly GRAY = '#cbd5e1';
  readonly colorScheme = {
    domain: [
      FlexiComplianceBusinessWidgetComponent.GREEN,
      FlexiComplianceBusinessWidgetComponent.YELLOW,
      FlexiComplianceBusinessWidgetComponent.RED,
      FlexiComplianceBusinessWidgetComponent.GRAY
    ]
  };

  constructor(
    private service: FlexiComplianceService,
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  /**
   * Open the business's full Flexi OHS Compliance page. Relative to the
   * dashboard route (`…/business/{id}/dashboard`), so `../Flexi/compliance`
   * resolves to `…/business/{id}/Flexi/compliance` — same pattern the other
   * dashboard widgets use to jump into a feature module. Sections always land
   * collapsed there (the compliance component resets its accordion on entry).
   */
  openCompliance(): void {
    this.router.navigate(['../Flexi/compliance'], { relativeTo: this.route });
  }

  ngOnInit(): void {
    this.service.getFinal(BaseServices.BusinessId).subscribe({
      next: agg => {
        this.loading = false;
        this.applyAggregate(agg);
      },
      // 404 / no completed review yet → neutral empty state, not an error.
      error: (_err: HttpErrorResponse) => {
        this.loading = false;
        this.resetToEmpty();
      }
    });
  }

  /**
   * True once we have a completed review with at least one checkpoint. Keyed
   * off the checkpoint count rather than the rated count: a completed review
   * whose checkpoints are all still unassessed is a real (fully gray) state
   * the card should show, not an empty one.
   */
  get hasData(): boolean {
    return this.total > 0;
  }

  private applyAggregate(agg: FlexiCompliance | null): void {
    // Only a published review carries a meaningful score breakdown — and
    // "published" means completedOn is set, NOT status === Completed. The
    // /final endpoint deliberately falls back to the most recent
    // ever-completed review, which goes to status Archived the moment the CSM
    // starts a new one; gating on the status here blanked the card at exactly
    // that moment, while the Flexi tab still showed the customer their score.
    if (!agg || !agg.completedOn || !agg.score) {
      this.resetToEmpty();
      return;
    }
    const score = agg.score;
    this.green = score.greenCount ?? 0;
    this.yellow = score.yellowCount ?? 0;
    this.red = score.redCount ?? 0;
    this.total = score.totalCount ??this.green + this.yellow + this.red;
    this.unrated = score.unratedCount ?? Math.max(0, this.total - (this.green + this.yellow + this.red));
    this.percentage = score.percentage ?? 0;
    this.smiley = score.smiley ?? null;
    this.completedOn = agg.completedOn ?? null;
    this.buildChart(score);
  }

  private resetToEmpty(): void {
    this.green = this.yellow = this.red = this.unrated = this.total = this.percentage = 0;
    this.smiley = null;
    this.completedOn = null;
    this.chartData = [];
    this.customColors = [];
  }

  /**
   * Segment values are the server's weight SHARES (0–100), not the raw
   * counts, so the ring reads as "how much of 100 % is compliant" rather
   * than "how do the rated items split among themselves".
   */
  private buildChart(score: FlexiCompliance['score']): void {
    const all = [
      {
        name: this.translate.instant('FLEXI_COMPLIANCE.RATINGS.GOOD'),
        value: score.greenShare ?? 0,
        color: FlexiComplianceBusinessWidgetComponent.GREEN
      },
      {
        name: this.translate.instant('FLEXI_COMPLIANCE.RATINGS.NEEDS_IMPROVEMENT'),
        value: score.yellowShare ?? 0,
        color: FlexiComplianceBusinessWidgetComponent.YELLOW
      },
      {
        name: this.translate.instant('FLEXI_COMPLIANCE.RATINGS.MISSING'),
        value: score.redShare ?? 0,
        color: FlexiComplianceBusinessWidgetComponent.RED
      },
      {
        name: this.translate.instant('FLEXI_COMPLIANCE.RATINGS.UNRATED'),
        value: score.unratedShare ?? 0,
        color: FlexiComplianceBusinessWidgetComponent.GRAY
      }
    ];
    // Suppress zero-width segments so the donut stays clean — including gray,
    // which is absent exactly when the review adds up to 100 % on its own.
    const visible = all.filter(s => s.value > 0);
    this.chartData = visible.map(({ name, value }) => ({ name, value }));
    this.customColors = visible.map(({ name, color }) => ({ name, value: color }));
  }

  smileyClass(): string {
    return this.smiley ? `is-${this.smiley}` : '';
  }

  /**
   * Face for the current bucket, shown beside the ring. Null when the review
   * carries no score, so the card renders no face at all rather than a default
   * one — a missing score is not the same as a bad one.
   */
  get faceImage(): string | null {
    return smileyImage(this.smiley);
  }

  /**
   * Translation key of the one-word verdict. Used as the image's accessible
   * name and its tooltip: the face carries the meaning here, so it cannot be
   * left as decoration for a screen reader.
   */
  get faceLabelKey(): string {
    return smileyLabelKey(this.smiley);
  }
}
